import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from './api';
import { useAuth } from './auth-context';
import { toast } from 'sonner';

const WishlistContext = createContext();

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (!context) {
    throw new Error('useWishlist must be used within a WishlistProvider');
  }
  return context;
};

export const WishlistProvider = ({ children }) => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [wishlistIds, setWishlistIds] = useState(new Set());

  // Fetch wishlist from server
  const fetchWishlist = useCallback(async () => {
    if (!user) {
      setItems([]);
      setWishlistIds(new Set());
      return;
    }

    try {
      setLoading(true);
      const response = await api.get('/wishlist');
      const wishlistItems = response.data.items || [];
      setItems(wishlistItems);
      setWishlistIds(new Set(wishlistItems.map(item => item.product_id)));
    } catch (error) {
      console.error('Failed to fetch wishlist:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Load wishlist on user change
  useEffect(() => {
    fetchWishlist();
  }, [fetchWishlist]);

  // Check if product is in wishlist
  const isInWishlist = useCallback((productId) => {
    return wishlistIds.has(productId);
  }, [wishlistIds]);

  // Toggle product in wishlist
  const toggleWishlist = useCallback(async (productId, productName) => {
    if (!user) {
      toast.error('Favorilere eklemek için giriş yapmalısınız');
      return { success: false, needsLogin: true };
    }

    try {
      const response = await api.post('/wishlist/toggle', { product_id: productId });
      const inWishlist = response.data.in_wishlist;

      // Update local state
      if (inWishlist) {
        setWishlistIds(prev => new Set([...prev, productId]));
        toast.success(productName ? `"${productName}" favorilere eklendi` : 'Favorilere eklendi');
      } else {
        setWishlistIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(productId);
          return newSet;
        });
        toast.success(productName ? `"${productName}" favorilerden çıkarıldı` : 'Favorilerden çıkarıldı');
      }

      // Refresh full list
      fetchWishlist();

      return { success: true, inWishlist };
    } catch (error) {
      console.error('Failed to toggle wishlist:', error);
      toast.error('İşlem başarısız oldu');
      return { success: false };
    }
  }, [user, fetchWishlist]);

  // Add to wishlist
  const addToWishlist = useCallback(async (productId, productName) => {
    if (!user) {
      toast.error('Favorilere eklemek için giriş yapmalısınız');
      return { success: false, needsLogin: true };
    }

    if (wishlistIds.has(productId)) {
      toast.info('Bu ürün zaten favorilerinizde');
      return { success: true, alreadyExists: true };
    }

    try {
      await api.post('/wishlist/add', { product_id: productId });
      setWishlistIds(prev => new Set([...prev, productId]));
      toast.success(productName ? `"${productName}" favorilere eklendi` : 'Favorilere eklendi');
      fetchWishlist();
      return { success: true };
    } catch (error) {
      console.error('Failed to add to wishlist:', error);
      toast.error('Favorilere eklenemedi');
      return { success: false };
    }
  }, [user, wishlistIds, fetchWishlist]);

  // Remove from wishlist (silent = true suppresses the toast)
  const removeFromWishlist = useCallback(async (productId, productName, silent = false) => {
    if (!user) return { success: false };

    try {
      await api.delete(`/wishlist/remove/${productId}`);
      setWishlistIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(productId);
        return newSet;
      });
      setItems(prev => prev.filter(item => item.product_id !== productId));
      if (!silent) {
        toast.success(productName ? `"${productName}" favorilerden çıkarıldı` : 'Favorilerden çıkarıldı');
      }
      return { success: true };
    } catch (error) {
      console.error('Failed to remove from wishlist:', error);
      if (!silent) {
        toast.error('Favorilerden çıkarılamadı');
      }
      return { success: false };
    }
  }, [user]);

  // Clear wishlist
  const clearWishlist = useCallback(async () => {
    if (!user) return { success: false };

    try {
      await api.delete('/wishlist/clear');
      setItems([]);
      setWishlistIds(new Set());
      toast.success('Favoriler temizlendi');
      return { success: true };
    } catch (error) {
      console.error('Failed to clear wishlist:', error);
      toast.error('Favoriler temizlenemedi');
      return { success: false };
    }
  }, [user]);

  const value = {
    items,
    loading,
    count: wishlistIds.size,
    isInWishlist,
    toggleWishlist,
    addToWishlist,
    removeFromWishlist,
    clearWishlist,
    refreshWishlist: fetchWishlist
  };

  return (
    <WishlistContext.Provider value={value}>
      {children}
    </WishlistContext.Provider>
  );
};

export default WishlistContext;
